import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, LinearProgress, Stack,
  Button, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, TextField, MenuItem, Skeleton, Divider, Chip, Avatar,
} from '@mui/material';
import {
  ResponsiveContainer, ComposedChart, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell,
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import PeopleIcon from '@mui/icons-material/People';
import EuroIcon from '@mui/icons-material/Euro';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiService from '../../services/apiServices';
import PageHeader from '../common/PageHeader';
import { printReport } from '../../utils/pdfPrint';
import SendReportEmailDialog from '../common/SendReportEmailDialog';
import { ReportPage, ExportMenu } from './ReportKit';
import { activeWorkers } from '../../utils/workerStatus';

/* helpers */
const fmt = (n) => Number(n || 0).toLocaleString('sq-XK', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtEur = (n) => `\u20ac ${fmt(n)}`;
const sum = (arr, fn) => (arr || []).reduce((a, x) => a + Number(fn(x) || 0), 0);

function dlFile(filename, rows, headers) {
  const lines = [headers.join(',')];
  for (const r of rows) lines.push(headers.map(h => `"${String(r?.[h] ?? '').replace(/"/g, '""')}"`).join(','));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const DEPT_COLORS  = ['#2563EB', '#0891B2', '#7C3AED', '#059669', '#B45309', '#DC2626'];
const LEAVE_COLORS = { Paid: '#16A34A', WithoutPay: '#DC2626', Medical: '#2563EB', Marital: '#D97706', Other: '#6B7280' };
const LEAVE_LABELS = { Paid: 'Me pages\u00eb', WithoutPay: 'Pa pages\u00eb', Medical: 'Mjek\u00ebsor', Marital: 'Martesor', Other: 'Tjet\u00ebr' }; // eslint-disable-line no-unused-vars
const LEAVE_LABEL_KEYS = { Paid: 'report.paidLeave', WithoutPay: 'report.unpaidLeave', Medical: 'report.medicalLeave', Marital: 'report.maritalLeave', Other: 'report.exceptionalLeave' };
const PIE_COLORS   = ['#2563EB', '#E11D48', '#F59E0B', '#10B981', '#8B5CF6'];

/* KPI Card */
function KpiCard({ icon: Icon, iconColor, label, value, sub, trend, loading }) {
  const trendIcon = trend > 0
    ? <TrendingUpIcon fontSize="small" sx={{ color: 'success.main' }} />
    : trend < 0
    ? <TrendingDownIcon fontSize="small" sx={{ color: 'error.main' }} />
    : <TrendingFlatIcon fontSize="small" sx={{ color: 'text.disabled' }} />;
  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>{label}</Typography>
            {loading
              ? <Skeleton width={80} height={36} />
              : <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.3, lineHeight: 1.2 }}>{value}</Typography>
            }
            {sub && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                {trend !== undefined && trendIcon}
                <Typography variant="caption" color="text.secondary">{sub}</Typography>
              </Stack>
            )}
          </Box>
          <Avatar sx={{ bgcolor: (iconColor || 'primary.main') + '20', color: iconColor || 'primary.main', width: 46, height: 46 }}>
            <Icon />
          </Avatar>
        </Stack>
      </CardContent>
    </Card>
  );
}

function SectionHeader({ title, action }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
      {action}
    </Stack>
  );
}

const CustomTooltip = ({ active, payload, label, currencyKeys = [] }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <Paper elevation={3} sx={{ p: 1.5, minWidth: 140, borderRadius: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>{label}</Typography>
      {payload.map((p) => (
        <Stack key={p.name} direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="caption" sx={{ color: p.color }}>{p.name}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {currencyKeys.includes(p.name) ? fmtEur(p.value) : fmt(p.value)}
          </Typography>
        </Stack>
      ))}
    </Paper>
  );
};

export default function Analytics() {
  const { t } = useTranslation();
  const [workers,      setWorkers]      = useState([]);
  const [contracts,    setContracts]    = useState([]);
  const [salaryLists,  setSalaryLists]  = useState([]);
  const [holidays,     setHolidays]     = useState([]);
  const [departments,  setDepartments]  = useState([]);
  const [tasks,        setTasks]        = useState([]);
  const [trainings,    setTrainings]    = useState([]);
  const [monthlyCosts, setMonthlyCosts] = useState([]);
  const [monthsRange,  setMonthsRange]  = useState(12);
  const [loading,      setLoading]      = useState(true);
  const [emailOpen,    setEmailOpen]    = useState(false);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        const [w, c, s, h, d, t, tr] = await Promise.all([
          apiService().get('workers'),
          apiService().get('workerContract'),
          apiService().get('salariesmonthlylists'),
          apiService().get('holidays'),
          apiService().get('department'),
          apiService().get('tasks'),
          apiService().get('trainings'),
        ]);
        if (!mounted) return;
        if (w?.success)  setWorkers(w.data || []);
        if (c?.success)  setContracts(c.data || []);
        if (s?.success)  setSalaryLists(s.data || []);
        if (h?.success)  setHolidays(h.data || []);
        if (d?.success)  setDepartments(d.data || []);
        if (t?.success)  setTasks(t.data || []);
        if (tr?.success) setTrainings(tr.data || []);
      } catch (_) {}
      finally { if (mounted) setLoading(false); }
    }
    load();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!salaryLists.length && !contracts.length) return;
    async function build() {
      const now = new Date();
      const months = [];
      for (let i = monthsRange - 1; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        months.push({ y: d.getFullYear(), m: d.getMonth() + 1, key: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}` });
      }
      const listsByMonth = new Map((salaryLists || []).map(l => [`${l.year||l.Year}-${String(l.month||l.Month).padStart(2,'0')}`, l]));
      const result = [];
      for (const t of months) {
        const mk = `${t.y}-${String(t.m).padStart(2,'0')}`;
        const monthStart = new Date(t.y, t.m - 1, 1);
        const monthEnd   = new Date(t.y, t.m, 0);
        let cost = 0;
        const l = listsByMonth.get(mk);
        if (l) {
          try {
            const r = await apiService().query('getSalariesByListId', `id=${l.id||l.Id}`);
            if (r?.success) {
              cost = (r.data || []).reduce((acc, b) => {
                const net     = Number(b.neto || b.NetSalary || b.net || 0);
                const tax     = Number(b.taxes || b.Tax || b.tax || 0);
                const contrib = Number(b.contributions || b.Contribution || 0);
                const gross   = Number(b.Salary || b.GrossSalary || b.bruto || 0);
                return acc + (net + tax + contrib > 0 ? net + tax + contrib : gross);
              }, 0);
            }
          } catch (_) {}
        }
        let headcount = 0;
        for (const c of (contracts || [])) {
          const s = c.StartDate ? new Date(c.StartDate) : null;
          const e = c.EndDate   ? new Date(c.EndDate)   : null;
          if (s && s <= monthEnd && (!e || e >= monthStart)) headcount++;
        }
        result.push({ month: mk, cost: +cost.toFixed(0), headcount });
      }
      setMonthlyCosts(result);
    }
    build();
  }, [salaryLists, contracts, monthsRange]);

  const kpis = useMemo(() => {
    const now = new Date();
    const activeCount = activeWorkers(workers).length;
    const lastCost = monthlyCosts.at(-1)?.cost ?? 0;
    const prevCost = monthlyCosts.length >= 2 ? monthlyCosts[monthlyCosts.length - 2]?.cost ?? 0 : 0;
    const atk = (workers || []).filter(w => w.ATKRegistered).length;
    const avgSalary = contracts.length
      ? Math.round(sum(contracts, c => c.Salary || c.BaseSalary || 0) / contracts.length) : 0;
    const leaveThisYear = (holidays || [])
      .filter(h => { const d = new Date(h.Date || h.StartDate || h.CreatedAt || 0); return d.getFullYear() === now.getFullYear(); })
      .reduce((a, h) => a + Number(h.holidayDays || h.HolidayDays || h.Days || 0), 0);
    const tasksDone  = (tasks || []).filter(t => t.Status === 'Done' || t.Status === 'Completed').length;
    const tasksTotal = tasks.length || 1;
    return {
      activeWorkers: activeCount, totalWorkers: workers.length,
      lastCost, prevCost,
      costTrend: prevCost > 0 ? ((lastCost - prevCost) / prevCost) : 0,
      avgSalary, atk, atkRate: workers.length ? Math.round((atk / workers.length) * 100) : 0,
      leaveThisYear, taskRate: Math.round((tasksDone / tasksTotal) * 100), tasksDone, tasksTotal,
    };
  }, [workers, contracts, monthlyCosts, holidays, tasks]);

  const deptStats = useMemo(() => {
    const deptMap = new Map((departments || []).map(d => [d.Id, d.Name]));
    const map = new Map();
    for (const w of (workers || [])) {
      const dId = w.Department || w.DepartmentId;
      const name = deptMap.get(dId) || w.DepartmentName || `Dept ${dId}`;
      if (!map.has(name)) map.set(name, { name, count: 0, totalSalary: 0 });
      map.get(name).count++;
    }
    for (const c of (contracts || [])) {
      const w = (workers || []).find(x => x.Id === (c.WorkerId || c.workerId));
      if (!w) continue;
      const dId = w.Department || w.DepartmentId;
      const name = deptMap.get(dId) || w.DepartmentName || `Dept ${dId}`;
      if (map.has(name)) map.get(name).totalSalary += Number(c.Salary || c.BaseSalary || 0);
    }
    return Array.from(map.values())
      .map(r => ({ ...r, avgSalary: r.count ? Math.round(r.totalSalary / r.count) : 0 }))
      .sort((a, b) => b.count - a.count);
  }, [workers, contracts, departments]);

  const leaveDist = useMemo(() => {
    const buckets = { Paid: 0, WithoutPay: 0, Medical: 0, Marital: 0, Other: 0 };
    for (const h of (holidays || [])) {
      const days = Number(h?.holidayDays || h?.HolidayDays || h?.Days || 0);
      switch (h?.HolidayType) {
        case 4: buckets.WithoutPay += days; break;
        case 3: buckets.Marital   += days; break;
        case 2: buckets.Medical   += days; break;
        case 5: buckets.Other     += days; break;
        default: buckets.Paid     += days;
      }
    }
    const total = Object.values(buckets).reduce((a, b) => a + b, 0) || 1;
    return Object.entries(buckets)
      .map(([key, value]) => ({ name: t(LEAVE_LABEL_KEYS[key] || key), value, pct: (value / total) * 100, color: LEAVE_COLORS[key] || '#6B7280' }))
      .filter(r => r.value > 0).sort((a, b) => b.value - a.value);
  }, [holidays]);

  const genderDist = useMemo(() => {
    // Gjinia ruhet si '1' (mashkull) / '0' (femër) nga RegisterWorker, por të
    // dhëna të vjetra mbajnë edhe 'M'/'F' ose 'male'/'female'. Krahasimi vetëm me
    // 'M'/'F' i numëronte të gjithë si "tjetër" — grafiku tregonte zero e zero
    // edhe kur fusha ishte e mbushur. E njëjta hartë si te WorkerDetail.js.
    const norm = (g) => {
      const s = String(g ?? '').trim().toLowerCase();
      if (s === '1' || s === 'm' || s === 'male'   || s === 'mashkull') return 'm';
      if (s === '0' || s === 'f' || s === 'female' || s === 'femër' || s === 'femer') return 'f';
      return '';
    };
    const m = (workers || []).filter(w => norm(w.Gender) === 'm').length;
    const f = (workers || []).filter(w => norm(w.Gender) === 'f').length;
    const o = workers.length - m - f;
    return [
      { name: t('report.male'),    value: m, color: '#2563EB' },
      { name: t('report.female'),  value: f, color: '#E11D48' },
      ...(o > 0 ? [{ name: t('report.other'), value: o, color: '#6B7280' }] : []),
    ].filter(r => r.value > 0);
  }, [workers]);

  const contractTypes = useMemo(() => {
    const map = new Map();
    for (const c of (contracts || [])) {
      const wt = c.WorkType || c.workType;
      const label = wt === 1 ? 'Full Time' : wt === 2 ? 'Part Time' : wt === 3 ? 'Me afat' : `Tip ${wt}`;
      map.set(label, (map.get(label) || 0) + 1);
    }
    return Array.from(map.entries()).map(([name, value], i) => ({ name, value, color: PIE_COLORS[i % PIE_COLORS.length] }));
  }, [contracts]);

  const taskStats = useMemo(() => {
    const st = {};
    for (const t of (tasks || [])) { const s = t.Status || 'Unknown'; st[s] = (st[s] || 0) + 1; }
    return Object.entries(st).map(([name, value], i) => ({ name, value, color: PIE_COLORS[i % PIE_COLORS.length] }));
  }, [tasks]);

  const trainingStats = useMemo(() => {
    const total     = trainings.length || 1;
    const completed  = (trainings || []).filter(t => t.Status === 'Completed' || t.Status === 'Done').length;
    const inProgress = (trainings || []).filter(t => (t.Status || '').toLowerCase().includes('progress')).length;
    const planned    = (trainings || []).filter(t => t.Status === 'Planned').length;
    return [
      { name: t('report.trainingCompleted'),   value: completed,  color: '#16A34A', pct: Math.round(completed/total*100)  },
      { name: t('report.trainingInProgress'),  value: inProgress, color: '#2563EB', pct: Math.round(inProgress/total*100) },
      { name: t('report.trainingPlanned'),     value: planned,    color: '#F59E0B', pct: Math.round(planned/total*100)    },
    ];
  }, [trainings]);

  const deptPie = deptStats.map((r, i) => ({ ...r, color: DEPT_COLORS[i % DEPT_COLORS.length] }));

  const summaryPdfCols = [
    { label: t('report.month'),       key: 'month',     align: 'left',  width: '34%' },
    { label: t('report.totalCost'),   key: 'cost',      align: 'right', format: 'currency', width: '33%' },
    { label: t('report.workers'),     key: 'headcount', align: 'right', width: '33%' },
  ];

  const exportPdf    = () => printReport({ title: t('reports.analytics'), subtitle: `${monthsRange} ${t('report.months')}`, orientation: 'portrait', columns: summaryPdfCols, rows: monthlyCosts });
  const exportDeptCsv = () => dlFile('departamentet.csv', deptStats, ['name', 'count', 'avgSalary', 'totalSalary']);

  return (
    <ReportPage>
      <PageHeader title={t('reports.analytics')} actions={
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <TextField select size="small" value={monthsRange} onChange={(e) => setMonthsRange(Number(e.target.value))} sx={{ minWidth: 100 }}>
            {[3, 6, 12, 18, 24].map(n => <MenuItem key={n} value={n}>{n} {t('report.months')}</MenuItem>)}
          </TextField>
          <ExportMenu onPdf={exportPdf} onEmail={() => setEmailOpen(true)} />
          <Button component={Link} to="/home/reports" variant="outlined" size="small">{t('report.backLabel')}</Button>
        </Stack>
      } />

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* KPI Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { icon: PeopleIcon,               iconColor: '#2563EB', label: t('report.workers'),           value: kpis.activeWorkers,       sub: `${kpis.totalWorkers - kpis.activeWorkers} ${t('report.inactive')}` },
          { icon: AccountBalanceWalletIcon, iconColor: '#059669', label: t('report.monthlyCost'),        value: fmtEur(kpis.lastCost),    sub: kpis.prevCost ? `${kpis.costTrend>=0?'+':''}${(kpis.costTrend*100).toFixed(1)}% vs ${t('report.prevMonth')}` : t('report.noData'), trend: kpis.costTrend },
          { icon: EuroIcon,                 iconColor: '#7C3AED', label: t('report.avgSalary'),          value: fmtEur(kpis.avgSalary),   sub: t('report.avgContractSalary') },
          { icon: BeachAccessIcon,          iconColor: '#D97706', label: t('report.leaveDaysYear'),      value: kpis.leaveThisYear,       sub: `${t('report.year')} ${new Date().getFullYear()}` },
          { icon: AssignmentTurnedInIcon,   iconColor: '#DC2626', label: t('report.taskCompletion'),     value: `${kpis.taskRate}%`,      sub: `${kpis.tasksDone} ${t('report.of')} ${kpis.tasksTotal} ${t('report.task')}` },
          { icon: CheckCircleOutlineIcon,   iconColor: '#0891B2', label: t('report.atkRegisteredPct'),   value: `${kpis.atkRate}%`,       sub: `${kpis.atk} ${t('report.of')} ${kpis.totalWorkers} ${t('report.workers')}` },
        ].map((kpi, i) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
            <KpiCard loading={loading} {...kpi} />
          </Grid>
        ))}
      </Grid>

      {/* Monthly Cost + Headcount Chart */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2.5, p: 2.5 }}>
        <SectionHeader title={`${t('report.monthlyCostHeadcount')} (${monthsRange} ${t('report.months')})`} />
        {loading ? <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 1 }} /> : (
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={monthlyCosts} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} tickLine={false} />
              <YAxis yAxisId="cost" orientation="left"  tickFormatter={v => `\u20ac${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis yAxisId="head" orientation="right" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <Tooltip content={<CustomTooltip currencyKeys={[t('report.cost')]} />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="cost" dataKey="cost"      name={t('report.cost')}    fill="#2563EB" fillOpacity={0.85} radius={[3,3,0,0]} barSize={monthsRange<=6?32:monthsRange<=12?20:12} />
              <Bar yAxisId="head" dataKey="headcount" name={t('report.workers')} fill="#F59E0B" fillOpacity={0.7}  radius={[3,3,0,0]} barSize={monthsRange<=6?16:monthsRange<=12?10:6} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Mid Row */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {/* Leave Dist */}
        <Grid item xs={12} md={6} lg={4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%', p: 2.5 }}>
            <SectionHeader title={t('report.leaveDistribution')} action={
              <ExportMenu onCsv={() => dlFile('pushimet.csv', leaveDist, ['name','value','pct'])} />
            } />
            <ResponsiveContainer width="100%" height={190}>
              <PieChart>
                <Pie data={leaveDist} cx="50%" cy="50%" outerRadius={78} paddingAngle={3} dataKey="value"
                  label={({ name, pct }) => `${pct.toFixed(0)}%`} labelLine>
                  {leaveDist.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [`${v} ${t('report.days')}`, n]} />
              </PieChart>
            </ResponsiveContainer>
            <Stack spacing={0.75} sx={{ mt: 1 }}>
              {leaveDist.map(r => (
                <Stack key={r.name} direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: r.color }} />
                    <Typography variant="caption">{r.name}</Typography>
                  </Stack>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{r.value} d · {r.pct.toFixed(0)}%</Typography>
                </Stack>
              ))}
            </Stack>
          </Card>
        </Grid>

        {/* Gender + Contract */}
        <Grid item xs={12} md={6} lg={4}>
          <Stack spacing={2} sx={{ height: '100%' }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5, flex: 1 }}>
              <SectionHeader title={t('report.gender')} />
              <Stack direction="row" alignItems="center" spacing={2}>
                <ResponsiveContainer width="45%" height={110}>
                  <PieChart>
                    <Pie data={genderDist} cx="50%" cy="50%" innerRadius={26} outerRadius={50} paddingAngle={4} dataKey="value">
                      {genderDist.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [v, n]} />
                  </PieChart>
                </ResponsiveContainer>
                <Stack spacing={0.75}>
                  {genderDist.map(r => (
                    <Stack key={r.name} direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: r.color }} />
                      <Typography variant="caption">{r.name}</Typography>
                      <Chip size="small" label={r.value} sx={{ height: 18, fontSize: 11, fontWeight: 700 }} />
                      <Typography variant="caption" color="text.secondary">
                        {workers.length ? (r.value/workers.length*100).toFixed(0) : 0}%
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Card>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5, flex: 1 }}>
              <SectionHeader title={t('report.contractType')} />
              <Stack direction="row" alignItems="center" spacing={2}>
                <ResponsiveContainer width="45%" height={110}>
                  <PieChart>
                    <Pie data={contractTypes} cx="50%" cy="50%" innerRadius={26} outerRadius={50} paddingAngle={4} dataKey="value">
                      {contractTypes.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [v, n]} />
                  </PieChart>
                </ResponsiveContainer>
                <Stack spacing={0.75}>
                  {contractTypes.map(r => (
                    <Stack key={r.name} direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: r.color }} />
                      <Typography variant="caption">{r.name}</Typography>
                      <Chip size="small" label={r.value} sx={{ height: 18, fontSize: 11, fontWeight: 700 }} />
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Card>
          </Stack>
        </Grid>

        {/* Dept Horizontal BarChart */}
        <Grid item xs={12} md={12} lg={4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%', p: 2.5 }}>
            <SectionHeader title={t('report.workersByDept')} />
            <ResponsiveContainer width="100%" height={245}>
              <BarChart data={deptPie} layout="vertical" margin={{ top: 0, right: 0, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={105} tickLine={false} />
                <Tooltip formatter={(v) => [v, t('report.workers')]} />
                <Bar dataKey="count" name={t('report.workers')} radius={[0,3,3,0]} barSize={18}>
                  {deptPie.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Grid>
      </Grid>

      {/* Tasks + Trainings */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={5}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5, height: '100%' }}>
            <SectionHeader title={t('report.taskStatus')} />
            <Stack direction="row" alignItems="center" spacing={2}>
              <ResponsiveContainer width="42%" height={150}>
                <PieChart>
                  <Pie data={taskStats} cx="50%" cy="50%" innerRadius={34} outerRadius={60} paddingAngle={3} dataKey="value">
                    {taskStats.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [v, n]} />
                </PieChart>
              </ResponsiveContainer>
              <Stack spacing={1.5} sx={{ flex: 1 }}>
                {taskStats.map(r => (
                  <Box key={r.name}>
                    <Stack direction="row" justifyContent="space-between">
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: r.color }} />
                        <Typography variant="caption">{r.name}</Typography>
                      </Stack>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>{r.value}</Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={Math.min(100,(r.value/(tasks.length||1))*100)}
                      sx={{ height: 4, borderRadius: 2, bgcolor: r.color+'20', '& .MuiLinearProgress-bar': { bgcolor: r.color } }} />
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} md={7}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5, height: '100%' }}>
            <SectionHeader title={t('report.trainings')} />
            <Stack spacing={1.5}>
              {trainingStats.map(r => (
                <Box key={r.name}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: r.color }} />
                      <Typography variant="body2">{r.name}</Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: r.color }}>{r.value} ({r.pct}%)</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={r.pct}
                    sx={{ height: 8, borderRadius: 4, bgcolor: r.color+'20', '& .MuiLinearProgress-bar': { bgcolor: r.color, borderRadius: 4 } }} />
                </Box>
              ))}
              <Divider sx={{ my: 0.5 }} />
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                {(trainings || []).slice(0, 4).map(tr => (
                  <Chip key={tr.Id} size="small" label={tr.Name} variant="outlined"
                    sx={{ fontSize: 11,
                      borderColor: tr.Status === 'Completed' ? 'success.main' : (tr.Status||'').includes('rogress') ? 'primary.main' : 'warning.main',
                      color:       tr.Status === 'Completed' ? 'success.main' : (tr.Status||'').includes('rogress') ? 'primary.main' : 'warning.main',
                    }} />
                ))}
              </Stack>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      {/* Dept Table */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5 }}>
        <SectionHeader title={t('report.breakdownByDept')} action={
          <ExportMenu onCsv={exportDeptCsv} />
        } />
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700 }}>{t('report.department')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{t('report.workers')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>%</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{t('report.avgSalary')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{t('report.totalCost')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({length:4}).map((_,i)=>(
                    <TableRow key={i}>{Array.from({length:5}).map((_,j)=><TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
                  ))
                : deptStats.map((r, i) => (
                    <TableRow key={r.name} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box sx={{ width:10,height:10,borderRadius:'50%',bgcolor:DEPT_COLORS[i%DEPT_COLORS.length] }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.name}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{r.count}</TableCell>
                      <TableCell align="right">{workers.length?(r.count/workers.length*100).toFixed(0):0}%</TableCell>
                      <TableCell align="right">{fmtEur(r.avgSalary)}</TableCell>
                      <TableCell align="right">{fmtEur(r.totalSalary)}</TableCell>
                    </TableRow>
                  ))
              }
              {!loading && deptStats.length > 0 && (
                <TableRow sx={{ bgcolor: 'action.selected' }}>
                  <TableCell sx={{ fontWeight: 700 }}>{t('report.total')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{workers.length}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>100%</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtEur(kpis.avgSalary)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtEur(deptStats.reduce((a,r)=>a+r.totalSalary,0))}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <SendReportEmailDialog
        open={emailOpen} onClose={() => setEmailOpen(false)}
        reportTitle={t('reports.analytics')} subtitle={`${monthsRange} ${t('report.months')}`}
        columns={summaryPdfCols} rows={monthlyCosts}
        workers={workers} orientation="portrait"
      />
    </ReportPage>
  );
}
